import { Injectable } from '@angular/core';
import { BehaviorTrackerScriptInjectorService } from 'app/behavior-tracking/behavior-tracker-script-injector.service';
import { environment } from 'environments/environment';
import { IBehaviorTrackingProvider } from '../interfaces/behavior-tracking-provider.interface';

declare const s: any;

declare global {
    interface Window {
        _satellite?: { track: (event: string, data?: unknown) => void };
    }
}

interface IAdobeMapping {
    eVars?: Record<string, string | number>;
    props?: Record<string, string | number>;
    events?: string[];
}

@Injectable()
export class AdobeTrackingProviderService implements IBehaviorTrackingProvider {
    public name?: string = 'adobe';
    private readonly useLaunch: boolean = false;

    public constructor(private scriptInjector: BehaviorTrackerScriptInjectorService) {
        if (!environment.feature.behaviorTrackingAnalytics?.enabled) {
            return;
        }

        const adobeConfig = environment.feature?.behaviorTrackingAnalytics?.adobe;
        if (!adobeConfig?.enabled) {
            return;
        }

        this.useLaunch = adobeConfig.useLaunch ?? false;
        if (this.useLaunch) {
            this.scriptInjector.injectScript(adobeConfig.script.launchUrl, 'adobe-launch');
            return;
        }

        this.scriptInjector.injectScript(adobeConfig.script.url, 'adobe-analytics');
        this.scriptInjector.injectNoScriptIframe(adobeConfig.noscript.url);
    }

    public capture(event: string, data: Record<string, unknown> = {}): void {
        if (this.useLaunch) {
            window._satellite?.track(event, data);
            return;
        }

        if (typeof s === 'undefined' || !s) {
            return;
        }

        this.buildAdobeVars(data);

        if (data.isPageView) {
            s.pageName = event;
            s.t();
        } else {
            s.tl(true, 'o', event);
        }
    }

    public identify?(userId: string, properties?: Record<string, unknown>): void {
        if (typeof s === 'undefined' || !s) {
            return;
        }

        s.eVar1 = userId;
        if (properties) {
            this.buildAdobeVars({ eVars: properties as Record<string, string | number> });
        }
    }

    private buildAdobeVars(mapping: IAdobeMapping): void {
        if (!s) {
            return;
        }

        if (mapping.eVars) {
            Object.entries(mapping.eVars).forEach(([key, val]) => {
                s[`eVar${ key }`] = val;
            });
        }

        if (mapping.props) {
            Object.entries(mapping.props).forEach(([key, val]) => {
                s[`prop${ key }`] = val;
            });
        }

        if (mapping.events && mapping.events.length) {
            s.events = mapping.events.join(',');
        }
    }
}
